import React, { useEffect, useMemo, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import { apiGet, apiPost } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { formatINR } from '../../lib/format';
import { toast } from 'sonner';
import {
  BookOpen, FilePlus, ListChecks, AlertOctagon, RefreshCw,
  PlusCircle, Trash2, Loader2, Check, X,
} from 'lucide-react';
import { useUrlTab } from '../../lib/hooks/useUrlState';

const TABS = [
  { key: 'tb', label: 'Trial Balance', icon: ListChecks },
  { key: 'ledger', label: 'Ledger', icon: BookOpen },
  { key: 'jv', label: 'Manual JV', icon: FilePlus },
  { key: 'tally', label: 'Tally Recon', icon: RefreshCw },
  { key: 'errors', label: 'Posting Errors', icon: AlertOctagon },
];

const todayMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const Pill = ({ children, tone = 'slate' }) => (
  <span className={`inline-flex px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase tracking-wider border
    ${tone === 'green' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
      tone === 'red' ? 'bg-rose-50 border-rose-200 text-rose-800' :
      tone === 'amber' ? 'bg-amber-50 border-amber-200 text-amber-800' :
      'bg-slate-50 border-slate-200 text-slate-600'}`}>{children}</span>
);

// ─────────────────────── Trial Balance ──────────────────────────────────────
function TrialBalanceTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      setData(await apiGet(`/finance/gl/trial-balance?show_all=${showAll}`));
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [showAll]);

  return (
    <div className="space-y-3" data-testid="gl-tb-tab">
      <div className="flex items-center justify-between text-xs">
        <label className="flex items-center gap-1.5 cursor-pointer">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)}
                 className="w-4 h-4 accent-[#3b4872]" data-testid="gl-tb-show-all" />
          Show all accounts (incl. zero-balance)
        </label>
        {data && (
          <div className="flex items-center gap-2">
            <span className="text-slate-500">Σ Dr: <b>{formatINR(data.totals.debit)}</b></span>
            <span className="text-slate-500">Σ Cr: <b>{formatINR(data.totals.credit)}</b></span>
            <Pill tone={data.totals.balanced ? 'green' : 'red'}>{data.totals.balanced ? 'Balanced' : 'Out of balance'}</Pill>
          </div>
        )}
      </div>
      {data?.meta?.note && (
        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-2">
          {data.meta.note}
        </div>
      )}
      {loading ? <div className="text-sm text-slate-500">Loading…</div> : (
        <table className="w-full text-xs">
          <thead className="text-left text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-1.5 px-2">Code</th>
              <th className="py-1.5 px-2">Account</th>
              <th className="py-1.5 px-2">Group</th>
              <th className="py-1.5 px-2 text-right">Period Dr</th>
              <th className="py-1.5 px-2 text-right">Period Cr</th>
              <th className="py-1.5 px-2 text-right">Closing</th>
            </tr>
          </thead>
          <tbody>
            {(data?.rows || []).map((r) => (
              <tr key={r.code} className="border-b border-slate-100">
                <td className="py-1.5 px-2 font-mono text-[11px]">{r.code}</td>
                <td className="py-1.5 px-2">{r.name}</td>
                <td className="py-1.5 px-2 text-slate-500 text-[11px]">{r.group}</td>
                <td className="py-1.5 px-2 text-right">{r.period_debit ? formatINR(r.period_debit) : '—'}</td>
                <td className="py-1.5 px-2 text-right">{r.period_credit ? formatINR(r.period_credit) : '—'}</td>
                <td className="py-1.5 px-2 text-right font-bold">{formatINR(r.closing_balance)}</td>
              </tr>
            ))}
            {(data?.rows || []).length === 0 && (
              <tr><td colSpan={6} className="py-6 text-center text-slate-400">No transactions yet</td></tr>
            )}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─────────────────────── Ledger ─────────────────────────────────────────────
function LedgerTab() {
  const [coa, setCoa] = useState([]);
  const [code, setCode] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => { apiGet('/finance/gl/coa').then((d) => setCoa(d.items || [])); }, []);
  const load = async () => {
    if (!code) return;
    try { setData(await apiGet(`/finance/gl/ledger/${code}`)); }
    catch (e) { toast.error(e.response?.data?.detail || 'Failed to load'); }
  };

  return (
    <div className="space-y-3" data-testid="gl-ledger-tab">
      <div className="flex flex-wrap items-center gap-2">
        <select value={code} onChange={(e) => setCode(e.target.value)}
                className="px-3 py-2 border border-slate-200 rounded-sm text-sm min-w-[280px]"
                data-testid="gl-ledger-account">
          <option value="">— select account —</option>
          {coa.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
        </select>
        <button onClick={load} disabled={!code}
                className="px-4 py-2 bg-[#3b4872] text-white text-xs uppercase font-bold rounded-sm disabled:opacity-40"
                data-testid="gl-ledger-load">View</button>
      </div>
      {data && (
        <>
          <div className="text-xs text-slate-600">
            <b>{data.account.code} · {data.account.name}</b>
            <span className="ml-3">Closing balance: <b>{formatINR(data.closing_balance)}</b></span>
          </div>
          <table className="w-full text-xs">
            <thead className="text-left text-slate-500 border-b border-slate-200">
              <tr>
                <th className="py-1.5 px-2">Date</th>
                <th className="py-1.5 px-2">JV No.</th>
                <th className="py-1.5 px-2">Narration</th>
                <th className="py-1.5 px-2 text-right">Dr</th>
                <th className="py-1.5 px-2 text-right">Cr</th>
                <th className="py-1.5 px-2 text-right">Running</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => (
                <tr key={i} className="border-b border-slate-100">
                  <td className="py-1.5 px-2 text-[11px]">{r.date}</td>
                  <td className="py-1.5 px-2 font-mono text-[11px]">{r.jv_no}</td>
                  <td className="py-1.5 px-2 text-slate-600">{r.narration}</td>
                  <td className="py-1.5 px-2 text-right">{r.debit ? formatINR(r.debit) : '—'}</td>
                  <td className="py-1.5 px-2 text-right">{r.credit ? formatINR(r.credit) : '—'}</td>
                  <td className="py-1.5 px-2 text-right font-bold">{formatINR(r.running_balance)}</td>
                </tr>
              ))}
              {data.rows.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-slate-400">No entries</td></tr>
              )}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}

// ─────────────────────── Manual JV ─────────────────────────────────────────
function ManualJVTab() {
  const [coa, setCoa] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [narration, setNarration] = useState('');
  const [lines, setLines] = useState([
    { account_code: '', debit: '', credit: '' },
    { account_code: '', debit: '', credit: '' },
  ]);
  const [posting, setPosting] = useState(false);

  useEffect(() => { apiGet('/finance/gl/coa').then((d) => setCoa(d.items || [])); }, []);

  const totalDr = useMemo(() => lines.reduce((s, l) => s + (Number(l.debit) || 0), 0), [lines]);
  const totalCr = useMemo(() => lines.reduce((s, l) => s + (Number(l.credit) || 0), 0), [lines]);
  const balanced = Math.abs(totalDr - totalCr) < 0.01 && totalDr > 0;

  const updateLine = (i, k, v) => {
    const next = [...lines];
    next[i] = { ...next[i], [k]: v };
    setLines(next);
  };

  const submit = async () => {
    if (!balanced) { toast.error('Journal is not balanced'); return; }
    setPosting(true);
    try {
      const r = await apiPost('/finance/gl/journals', {
        date, narration,
        lines: lines
          .filter((l) => l.account_code && (Number(l.debit) > 0 || Number(l.credit) > 0))
          .map((l) => ({ account_code: l.account_code,
                         debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 })),
      });
      toast.success(`Posted ${r.jv_no}`);
      setLines([{ account_code: '', debit: '', credit: '' }, { account_code: '', debit: '', credit: '' }]);
      setNarration('');
    } catch (e) { toast.error(e.response?.data?.detail || 'Posting failed'); }
    setPosting(false);
  };

  return (
    <div className="space-y-3" data-testid="gl-jv-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-[11px] text-slate-500 uppercase font-bold mb-1">Date</label>
          <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                 className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="jv-date" />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[11px] text-slate-500 uppercase font-bold mb-1">Narration</label>
          <input value={narration} onChange={(e) => setNarration(e.target.value)}
                 placeholder="Why this JV?"
                 className="w-full px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="jv-narration" />
        </div>
      </div>
      <table className="w-full text-xs">
        <thead className="text-left text-slate-500 border-b border-slate-200">
          <tr>
            <th className="py-1.5 px-2">Account</th>
            <th className="py-1.5 px-2 text-right">Debit</th>
            <th className="py-1.5 px-2 text-right">Credit</th>
            <th className="py-1.5 px-2"></th>
          </tr>
        </thead>
        <tbody>
          {lines.map((l, i) => (
            <tr key={i} className="border-b border-slate-100">
              <td className="py-1 px-2">
                <select value={l.account_code} onChange={(e) => updateLine(i, 'account_code', e.target.value)}
                        className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs"
                        data-testid={`jv-line-${i}-account`}>
                  <option value="">— select —</option>
                  {coa.map((a) => <option key={a.code} value={a.code}>{a.code} · {a.name}</option>)}
                </select>
              </td>
              <td className="py-1 px-2"><input type="number" min="0" value={l.debit}
                onChange={(e) => updateLine(i, 'debit', e.target.value)}
                className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs text-right"
                data-testid={`jv-line-${i}-debit`} /></td>
              <td className="py-1 px-2"><input type="number" min="0" value={l.credit}
                onChange={(e) => updateLine(i, 'credit', e.target.value)}
                className="w-full px-2 py-1.5 border border-slate-200 rounded-sm text-xs text-right"
                data-testid={`jv-line-${i}-credit`} /></td>
              <td className="py-1 px-2 text-center">
                {lines.length > 2 && (
                  <button onClick={() => setLines(lines.filter((_, j) => j !== i))}
                          className="text-rose-600 hover:text-rose-800" aria-label="Remove line"
                          data-testid={`jv-line-${i}-remove`}><Trash2 size={13} /></button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="font-bold">
            <td className="py-2 px-2 text-right">Totals</td>
            <td className="py-2 px-2 text-right" data-testid="jv-total-debit">{formatINR(totalDr)}</td>
            <td className="py-2 px-2 text-right" data-testid="jv-total-credit">{formatINR(totalCr)}</td>
            <td></td>
          </tr>
        </tfoot>
      </table>
      <div className="flex items-center justify-between">
        <button onClick={() => setLines([...lines, { account_code: '', debit: '', credit: '' }])}
                className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded-sm text-xs"
                data-testid="jv-add-line"><PlusCircle size={13} /> Add line</button>
        <div className="flex items-center gap-3">
          <Pill tone={balanced ? 'green' : 'amber'}>{balanced ? 'Balanced' : 'Not balanced'}</Pill>
          <button onClick={submit} disabled={!balanced || posting}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[#3b4872] text-white text-xs uppercase font-bold rounded-sm disabled:opacity-40"
                  data-testid="jv-submit">
            {posting ? <Loader2 size={14} className="animate-spin" /> : <Check size={14} />}
            Post Journal
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────── Tally Recon ───────────────────────────────────────
function TallyReconTab() {
  const [month, setMonth] = useState(todayMonth());
  const [data, setData] = useState(null);
  useEffect(() => {
    apiGet(`/finance/gl/tally-recon?month=${month}`).then(setData).catch(() => setData(null));
  }, [month]);
  return (
    <div className="space-y-3" data-testid="gl-tally-tab">
      <div className="flex items-center gap-3">
        <input type="month" value={month} onChange={(e) => setMonth(e.target.value)}
               className="px-3 py-2 border border-slate-200 rounded-sm text-sm" data-testid="tally-month" />
        {data?.summary && (
          <>
            <span className="text-xs text-slate-500">Matched: <b>{data.summary.matched}</b> / {data.summary.total_accounts}</span>
            <span className="text-xs text-slate-500">Variance: <b>{formatINR(data.summary.variance_total)}</b></span>
          </>
        )}
      </div>
      {(!data || data.rows.length === 0) ? (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3">
          No accounts mapped to Tally ledgers yet. Map a Tally ledger to a GL account from Settings → GL Accounts.
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-left text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-1.5 px-2">Code</th>
              <th className="py-1.5 px-2">GL Account</th>
              <th className="py-1.5 px-2">Tally Ledger</th>
              <th className="py-1.5 px-2 text-right">GL Bal</th>
              <th className="py-1.5 px-2 text-right">Tally Bal</th>
              <th className="py-1.5 px-2 text-right">Variance</th>
              <th className="py-1.5 px-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.rows.map((r) => (
              <tr key={r.code} className="border-b border-slate-100">
                <td className="py-1.5 px-2 font-mono">{r.code}</td>
                <td className="py-1.5 px-2">{r.name}</td>
                <td className="py-1.5 px-2 text-slate-500">{r.maps_to_tally_ledger}</td>
                <td className="py-1.5 px-2 text-right">{formatINR(r.gl_balance)}</td>
                <td className="py-1.5 px-2 text-right">{formatINR(r.tally_balance)}</td>
                <td className={`py-1.5 px-2 text-right font-bold ${Math.abs(r.variance) > 0.01 ? 'text-rose-700' : 'text-emerald-700'}`}>{formatINR(r.variance)}</td>
                <td className="py-1.5 px-2">{r.matched ? <Pill tone="green">Match</Pill> : <Pill tone="red">Variance</Pill>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─────────────────────── Posting Errors ────────────────────────────────────
function PostingErrorsTab() {
  const [resolved, setResolved] = useState(false);
  const [data, setData] = useState(null);
  const [retrying, setRetrying] = useState({});

  const load = () => apiGet(`/finance/gl/posting-errors?resolved=${resolved}`).then(setData);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [resolved]);

  const retry = async (id) => {
    setRetrying({ ...retrying, [id]: true });
    try {
      const r = await apiPost(`/finance/gl/posting-errors/${id}/retry`, {});
      toast.success(`Retried → ${r.jv_no}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
    setRetrying({ ...retrying, [id]: false });
  };

  return (
    <div className="space-y-3" data-testid="gl-errors-tab">
      <label className="flex items-center gap-1.5 text-xs cursor-pointer">
        <input type="checkbox" checked={resolved} onChange={(e) => setResolved(e.target.checked)}
               className="w-4 h-4 accent-[#3b4872]" data-testid="errors-show-resolved" />
        Show resolved
      </label>
      {(!data?.items || data.items.length === 0) ? (
        <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-sm p-3 flex items-center gap-2">
          <Check size={14} className="text-emerald-600" /> No {resolved ? 'resolved' : 'unresolved'} posting errors.
        </div>
      ) : (
        <table className="w-full text-xs">
          <thead className="text-left text-slate-500 border-b border-slate-200">
            <tr>
              <th className="py-1.5 px-2">Logged At</th>
              <th className="py-1.5 px-2">Event</th>
              <th className="py-1.5 px-2">Source</th>
              <th className="py-1.5 px-2">Error</th>
              <th className="py-1.5 px-2"></th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((e) => (
              <tr key={e.id} className="border-b border-slate-100">
                <td className="py-1.5 px-2 text-[11px]">{(e.logged_at || '').slice(0, 16)}</td>
                <td className="py-1.5 px-2 font-mono text-[11px]">{e.event}</td>
                <td className="py-1.5 px-2 text-[11px]">{e.source_doc_type} · {(e.source_doc_id || '').slice(0, 8)}</td>
                <td className="py-1.5 px-2 text-rose-700">{e.error}</td>
                <td className="py-1.5 px-2 text-right">
                  {!e.resolved && (
                    <button onClick={() => retry(e.id)} disabled={retrying[e.id]}
                            className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 rounded-sm text-[11px] disabled:opacity-40"
                            data-testid={`error-retry-${e.id}`}>
                      {retrying[e.id] ? <Loader2 size={11} className="animate-spin" /> : <RefreshCw size={11} />}
                      Retry
                    </button>
                  )}
                  {e.resolved && <Pill tone="green">Resolved → {e.resolved_jv_no}</Pill>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ─────────────────────── Page shell ────────────────────────────────────────
export default function ShadowGL() {
  const [tab, setTab] = useUrlTab('tab', 'tb');
  return (
    <div className="p-6 space-y-5" data-testid="gl-page">
      <PageHeader
        title="General Ledger (Shadow)"
        subtitle="Auto-posted journals from approved invoices, payments & receipts. Tally remains the books-of-record until the recon shows zero variance."
        icon={BookOpen}
      />
      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.key} onClick={() => setTab(t.key)}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-display font-bold border-b-2 transition
                      ${tab === t.key ? 'border-[#3b4872] text-[#3b4872]' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                    data-testid={`gl-tab-${t.key}`}>
              <Icon size={13} /> {t.label}
            </button>
          );
        })}
      </div>
      <Card>
        <CardContent className="pt-5">
          {tab === 'tb' && <TrialBalanceTab />}
          {tab === 'ledger' && <LedgerTab />}
          {tab === 'jv' && <ManualJVTab />}
          {tab === 'tally' && <TallyReconTab />}
          {tab === 'errors' && <PostingErrorsTab />}
        </CardContent>
      </Card>
    </div>
  );
}
